import { Badge, Button, Checkbox, Collapse, Popover, Space } from 'antd';
import React, { useEffect } from 'react';
import useReportLog from '../../../hooks/useReportLog';
import { capitalizeFirstLetter } from '@qlibs/react-components';
import { DownOutlined, TableOutlined } from '@ant-design/icons';
import { useAuthUser } from 'react-auth-kit';

interface IProps {
  columns: any[];
}

const TableConfiguration: React.FC<IProps> = ({ columns }) => {
  const { checklistColumn, setChecklistColumn } = useReportLog();
  const auth = useAuthUser();
  const user = auth();

  const saveColumnSettings = (hiddenColumns: string[]) => {
    if (user?.userId) {
      const settings = {
        userId: user.userId,
        hiddenColumns,
        lastUpdated: new Date().toISOString(),
      };
      localStorage.setItem(
        `report-column-settings-${user.userId}`,
        JSON.stringify(settings)
      );
    }
  };

  useEffect(() => {
    if (user?.userId && columns.length > 0) {
      const savedSettings = localStorage.getItem(
        `report-column-settings-${user.userId}`
      );
      if (savedSettings) {
        try {
          const { hiddenColumns } = JSON.parse(savedSettings);
          const allColumnKeys: string[] = [];
          columns.forEach((item: any) => {
            allColumnKeys.push(item.key);
            if (item.children) {
              item.children.forEach((child: any) => {
                allColumnKeys.push(child.key);
              });
            }
          });
          const visibleColumns = allColumnKeys.filter(
            (key) => !hiddenColumns.includes(key)
          );
          setChecklistColumn(visibleColumns);
        } catch (error) {
          console.error('Error loading column settings:', error);
        }
      }
    }
  }, [user?.userId, columns.length]); 

  const getHiddenColumnsCount = (): number => {
    let totalColumns = 0;
    const allChildKeys: string[] = [];
    
    columns.forEach((item: any) => {
      if (item.children) {
        totalColumns += item.children.length;
        item.children.forEach((child: any) => {
          allChildKeys.push(child.key);
        });
      } else if (item.key !== 'dateTime') {
        totalColumns += 1;
        allChildKeys.push(item.key);
      }
    });
    
    const visibleCount = checklistColumn.filter((key) => 
      allChildKeys.includes(key)
    ).length;
    
    return totalColumns - visibleCount;
  };

  const _renderCheckboxColumn = () => {
    const onChangeChildren = (childKey: string) => {
      console.log('childKey', checklistColumn, childKey);
      let newChecklistColumn: string[];
      if (!checklistColumn.includes(childKey)) {
        newChecklistColumn = [...checklistColumn, childKey];
        setChecklistColumn(newChecklistColumn);
        console.log('childKey 1', newChecklistColumn);
      } else {
        newChecklistColumn = checklistColumn.filter(
          (item) => ![childKey].includes(item)
        );
        setChecklistColumn(newChecklistColumn);
        console.log('childKey 2', newChecklistColumn);
      }
      const allColumnKeys: string[] = [];
      columns.forEach((item: any) => {
        allColumnKeys.push(item.key);
        if (item.children) {
          item.children.forEach((child: any) => {
            allColumnKeys.push(child.key);
          });
        }
      });
      const hiddenColumns = allColumnKeys.filter(
        (key) => !newChecklistColumn.includes(key)
      );
      saveColumnSettings(hiddenColumns);
    };

    const onChangeParent = (parentKey: string) => {
      const parent = columns.find((item: any) => item.key === parentKey);
      let childrens: string[] = [];
      if (parent.children) {
        childrens = parent.children.map((item: any) => item.key);
      }

      let newChecklistColumn: string[];
      if (!checklistColumn.includes(parentKey)) {
        newChecklistColumn = [...checklistColumn, ...childrens, parentKey];
        setChecklistColumn(newChecklistColumn);
      } else {
        newChecklistColumn = checklistColumn.filter(
          (item) => ![...childrens, parentKey].includes(item)
        );
        setChecklistColumn(newChecklistColumn);
      }
      const allColumnKeys: string[] = [];
      columns.forEach((item: any) => {
        allColumnKeys.push(item.key);
        if (item.children) {
          item.children.forEach((child: any) => {
            allColumnKeys.push(child.key);
          });
        }
      });
      const hiddenColumns = allColumnKeys.filter(
        (key) => !newChecklistColumn.includes(key)
      );
      saveColumnSettings(hiddenColumns);
    };

    const items = columns?.map((item: any, idx: number) => {
      let checkboxOption: { label: string; value: string }[] = [];

      if (item.children) {
        checkboxOption = item.children.map((el: any) => ({
          label: capitalizeFirstLetter(el.title),
          value: el.key,
        }));
      }

      return {
        key: idx,
        showArrow: item.children ? true : false,
        label: (
          <Checkbox
            key={item.key}
            disabled={item.key === 'dateTime'}
            checked={checklistColumn.includes(item.key)}
            onChange={() => onChangeParent(item.key)}
          >
            {capitalizeFirstLetter(item.title)}
          </Checkbox>
        ),
        children:
          item.children &&
          item.children.map((item: any) => {
            return (
              <Checkbox
                key={item.key}
                checked={checklistColumn.includes(item.key)}
                onChange={() => onChangeChildren(item.key)}
              >
                {capitalizeFirstLetter(item.title)}
              </Checkbox>
            );
          }),
      };
    });

    return (
      <Collapse
        className='checkbox-table-report-log'
        collapsible={'icon'}
        items={items}
        size='small'
        style={{ width: 300 }}
      />
    );
  };

  return (
    <React.Fragment>
      <Popover
        content={_renderCheckboxColumn()}
        trigger='hover'
        overlayInnerStyle={{
          width: 320,
        }}
        placement='bottomRight'
      >
        <Badge count={getHiddenColumnsCount()} offset={[-5, 5]} color='red'>
          <Button color='default' type='primary' icon={<TableOutlined />}>
            <Space>
              Show/Hide Column
              {getHiddenColumnsCount() > 0 && (
                <span style={{ fontSize: '12px', opacity: 0.8 }}>
                  ({getHiddenColumnsCount()} hidden)
                </span>
              )}
              <DownOutlined />
            </Space>
          </Button>
        </Badge>
      </Popover>
    </React.Fragment>
  );
};

export default TableConfiguration;
